import { DiffType, GetCssClassNamesOption } from '@ardoq/data-model';
import { referenceInterface } from '@ardoq/reference-interface';
import { GraphItem, GraphItemMetadata } from './GraphItem';
import SparkMD5 from 'spark-md5';
import { ItemLabels } from '../types';
import { LineType } from '@ardoq/api-types';
import { DIFF_TYPE } from '@ardoq/global-consts';
import { GraphEdgeBase } from './GraphEdgeBase';
import { ComponentGraphEdgeModel } from './types';
import { getGraphNodes } from './utils';

export class GraphEdge
  extends GraphEdgeBase
  implements ComponentGraphEdgeModel
{
  static create(
    modelId: string,
    sourceId: string,
    targetId: string,
    graphNodeMap: Map<string, GraphItem>,
    id?: string,
    itemLabels?: ItemLabels
  ) {
    const { sourceGraphNode, targetGraphNode } = getGraphNodes(
      sourceId,
      targetId,
      graphNodeMap
    );
    return new GraphEdge(
      modelId,
      sourceGraphNode.id,
      targetGraphNode.id,
      sourceGraphNode.modelId,
      targetGraphNode.modelId,
      id,
      itemLabels
    );
  }

  readonly source: string;
  readonly target: string;
  readonly sourceModelId: string;
  readonly targetModelId: string;

  protected itemLabels: ItemLabels | undefined;

  private isSelected = false;
  private isInSelectedPath = false;
  private hasCollapsingRuleError = false;
  private isVirtual = false;

  private constructor(
    modelId: string,
    sourceGraphNodeId: string,
    targetGraphNodeId: string,
    sourceModelId: string,
    targetModelId: string,
    id?: string,
    itemLabels?: ItemLabels
  ) {
    const graphItemMetadata: GraphItemMetadata = {
      isComponent: false,
      isReference: referenceInterface.isReference(modelId),
      isGroup: false,
    };
    super(modelId, graphItemMetadata, id);
    this.source = sourceGraphNodeId;
    this.target = targetGraphNodeId;
    this.sourceModelId = sourceModelId;
    this.targetModelId = targetModelId;
    this.itemLabels = itemLabels;
    this.hash = SparkMD5.hash(this.getRawHash());
  }
  override getVisualDiffType(): DiffType {
    return referenceInterface.get(this.modelId)?.[DIFF_TYPE] ?? DiffType.NONE;
  }
  override getCSS(options?: GetCssClassNamesOption) {
    return referenceInterface.getCssClassNames(this.modelId) ?? '';
  }
  override hasURLFields() {
    return referenceInterface.hasURLFields(this.modelId);
  }
  override getItemLabels(): ItemLabels | undefined {
    return this.itemLabels;
  }
  override setItemLabels(itemLabels: ItemLabels): void {
    this.itemLabels = itemLabels;
  }
  override getLabel() {
    return referenceInterface.getDisplayLabel(this.modelId) ?? '';
  }

  getLineBeginning() {
    return referenceInterface.getLineBeginning(this.modelId);
  }

  getLineEnding() {
    return referenceInterface.getLineEnding(this.modelId);
  }

  getStyle() {
    return referenceInterface.getStyle(this.modelId) ?? '';
  }

  getLine(): LineType {
    return (
      referenceInterface.getModelType(this.modelId)?.line ?? LineType.SOLID
    );
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.getLineBeginning()}${this.getLineEnding()}${
      this.sourceModelId
    }${this.targetModelId}`;
  }

  setIsSelected(isSelected: boolean) {
    this.isSelected = isSelected;
  }

  getIsSelected() {
    return this.isSelected;
  }

  setIsInSelectedPath(isSelected: boolean) {
    this.isInSelectedPath = isSelected;
  }

  getIsInSelectedPath() {
    return this.isInSelectedPath;
  }

  setHasCollapsingRuleError(hasError: boolean) {
    this.hasCollapsingRuleError = hasError;
  }

  getHasCollapsingRuleError() {
    return this.hasCollapsingRuleError;
  }

  setIsVirtual(isVirtual: boolean) {
    this.isVirtual = isVirtual;
  }

  getIsVirtual() {
    return this.isVirtual;
  }
}
